import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getProgramIdentity } from '../api/sacada';

const defaultIdentity = {
  mode: 'simulation',
  active_application: null,
  active_application_label: null,
  identity_stable: true,
  all_applications_available: true,
};

const ProgramIdentityContext = createContext({
  identity: defaultIdentity,
  loading: false,
  error: null,
  isApplicationActive: () => true,
});

export function ProgramIdentityProvider({ children }) {
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const refresh = async () => {
      try {
        const nextIdentity = await getProgramIdentity();
        if (!cancelled) {
          setIdentity(nextIdentity);
          setError(null);
          setLoading(false);
        }
      } catch (requestError) {
        if (!cancelled) {
          setIdentity(null);
          setError(requestError);
          setLoading(false);
        }
      } finally {
        if (!cancelled) timer = window.setTimeout(refresh, 2000);
      }
    };

    refresh();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const isApplicationActive = useCallback((application) => {
    if (!identity) return false;
    if (identity.all_applications_available || identity.mode === 'simulation') return true;
    return identity.identity_stable && identity.active_application === application;
  }, [identity]);

  const value = useMemo(() => ({
    identity,
    loading,
    error,
    isApplicationActive,
  }), [identity, loading, error, isApplicationActive]);

  return (
    <ProgramIdentityContext.Provider value={value}>
      {children}
    </ProgramIdentityContext.Provider>
  );
}

export function useProgramIdentity() {
  return useContext(ProgramIdentityContext);
}
